<template>
    <div class="w-full h-full flex flex-col text-left bg-gray-50">
        <form @submit.prevent="submitForm" class="flex-1 flex flex-col overflow-y-auto">
            <div class="p-6 space-y-6">
                <!-- Basic Information Section -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center mb-6">
                        <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                            <font-awesome-icon icon="briefcase" class="text-white" />
                        </div>
                        <h3 class="text-lg font-semibold text-gray-900">Employment Request Information</h3>
                    </div>

                    <div class="space-y-5">
                        <!-- Are you looking for a job? -->
                        <div>
                            <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                <font-awesome-icon icon="question-circle" class="mr-2 text-blue-600" />
                                Are you looking for a job? <span class="text-red-500 ml-1">*</span>
                            </label>
                            <div class="flex space-x-4 mt-2">
                                <label class="flex items-center cursor-pointer">
                                    <input type="radio" v-model="form.is_looking_for_job" :value="true"
                                        class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                    <span class="ml-2 text-gray-700">Yes</span>
                                </label>
                                <label class="flex items-center cursor-pointer">
                                    <input type="radio" v-model="form.is_looking_for_job" :value="false"
                                        class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                    <span class="ml-2 text-gray-700">No</span>
                                </label>
                            </div>
                        </div>

                        <!-- Not looking for job - Employed / Self-Employed options -->
                        <template v-if="form.is_looking_for_job === false">
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="briefcase" class="mr-2 text-blue-600" />
                                    Current Status <span class="text-red-500 ml-1">*</span>
                                </label>
                                <select v-model="form.not_looking_status"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                    <option value="">Select Current Status</option>
                                    <option value="Employed">Employed</option>
                                    <option value="Self-Employed / Business Owner">Self-Employed / Business Owner</option>
                                </select>
                            </div>

                            <!-- Employment details - when Employed or Self-Employed selected -->
                            <div v-if="form.not_looking_status"
                                class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Employer <span
                                            class="text-red-500">*</span></label>
                                    <div :class="{ 'border-red-300 rounded-lg': !employerIsValid }">
                                        <multiselect
                                            v-model="form.employerVal"
                                            :options="listEmployers"
                                            :custom-label="customLabelEmployer"
                                            placeholder="Search Employer"
                                            label="name"
                                            track-by="id"
                                            aria-label="Search Employer"
                                        ></multiselect>
                                    </div>
                                    <input v-if="isOtherEmployer" v-model="form.otherEmployer" type="text"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white mt-2"
                                        placeholder="Enter Employer Name" />
                                    <transition name="form-fade" mode="out-in">
                                        <div v-if="!employerIsValid" class="mt-1">
                                            <p class="text-xs text-red-600">{{ employerErrorMsg }}</p>
                                            <button v-if="employerSimilarNames.length > 0" type="button"
                                                @click="confirmEmployerOtherDifferent"
                                                class="mt-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors">
                                                No, my employer is different
                                            </button>
                                        </div>
                                    </transition>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Sector <span
                                            class="text-red-500">*</span></label>
                                    <select v-model="form.sector"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Select Sector</option>
                                        <option value="Government">Government</option>
                                        <option value="Semi-Government">Semi-Government</option>
                                        <option value="Private">Private</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Location <span
                                            class="text-red-500">*</span></label>
                                    <select v-model="form.location"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Select Location</option>
                                        <option value="Abu Dhabi">Abu Dhabi</option>
                                        <option value="Dubai">Dubai</option>
                                        <option value="Sharjah">Sharjah</option>
                                        <option value="Ajman">Ajman</option>
                                        <option value="Umm Al Quwain">Umm Al Quwain</option>
                                        <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                                        <option value="Fujairah">Fujairah</option>
                                        <option value="Al Ain">Al Ain</option>
                                        <option value="Al Dhafra Region">Al Dhafra Region</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Position <span
                                            class="text-red-500">*</span></label>
                                    <input v-model="form.position" type="text" placeholder="Job title"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Salary Range <span
                                            class="text-red-500">*</span></label>
                                    <select v-model="form.salary_range"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Select Salary Range</option>
                                        <option value="Less than 9,000 AED">Less than 9,000 AED</option>
                                        <option value="10-30,000 AED">10-30,000 AED</option>
                                        <option value="31-60,000 AED">31-60,000 AED</option>
                                        <option value="61-90,000 AED">61-90,000 AED</option>
                                        <option value="More than 91,000 AED">More than 91,000 AED</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">How many job
                                        offers <span class="text-red-500">*</span></label>
                                    <input v-model.number="form.job_offers_count" type="number" min="0"
                                        placeholder="0"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">How many
                                        interviews <span class="text-red-500">*</span></label>
                                    <input v-model.number="form.interviews_count" type="number" min="0"
                                        placeholder="0"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Got Job Within
                                        <span class="text-red-500">*</span></label>
                                    <select v-model="form.got_job_within"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Select an option</option>
                                        <option value="Before graduation">Before graduation</option>
                                        <option value="Within 3 months">Within 3 months</option>
                                        <option value="Within 6 months">Within 6 months</option>
                                        <option value="Within 9 months">Within 9 months</option>
                                        <option value="Within 12 months">Within 12 months</option>
                                        <option value="After 1 year">After 1 year</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Job related to
                                        the major <span class="text-red-500">*</span></label>
                                    <div class="flex space-x-4 mt-2">
                                        <label class="flex items-center cursor-pointer">
                                            <input type="radio" v-model="form.is_job_rel_major" :value="true"
                                                class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                            <span class="ml-2 text-gray-700">Yes</span>
                                        </label>
                                        <label class="flex items-center cursor-pointer">
                                            <input type="radio" v-model="form.is_job_rel_major" :value="false"
                                                class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300" />
                                            <span class="ml-2 text-gray-700">No</span>
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Contract Type
                                        <span class="text-red-500">*</span></label>
                                    <select v-model="form.contract_type"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                        <option value="">Select Contract Type</option>
                                        <option value="Permenant">Permenant</option>
                                        <option value="Temperory">Temperory</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Start Date <span
                                            class="text-red-500">*</span></label>
                                    <input v-model="form.start_date" type="date"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                </div>
                                <div>
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">End Date <span
                                            v-if="!form.is_currently_working" class="text-red-500">*</span></label>
                                    <input v-model="form.end_date" type="date"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                    <p class="text-xs text-gray-500 mt-1">Required when not currently working
                                        here</p>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2 cursor-pointer">
                                        <input type="checkbox" v-model="form.is_currently_working"
                                            class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2" />
                                        Currently working here <span class="text-red-500 ml-1">*</span>
                                    </label>
                                    <p class="text-xs text-gray-500">If unchecked, End Date is required</p>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">How did you get
                                        this job <span class="text-red-500">*</span></label>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <select v-model="form.job_resource"
                                            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                            <option value="">Select an option</option>
                                            <option value="University / College career events">University /
                                                College career events</option>
                                            <option value="College Alumni Portal">College Alumni Portal</option>
                                            <option value="External Career Exhibitions">External Career
                                                Exhibitions</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        <div v-if="form.job_resource === 'Other'">
                                            <input v-model="form.job_resource_other" type="text"
                                                placeholder="Please specify"
                                                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                                        </div>
                                    </div>
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-sm font-semibold text-gray-700 mb-1">Upload Job Letter
                                        (Optional)</label>
                                    <div
                                        class="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                                        <input @change="handleJobLetter" type="file"
                                            accept="image/*,application/pdf"
                                            class="w-full text-sm text-gray-500" />
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Conditional fields - only show if looking for job -->
                        <template v-if="form.is_looking_for_job">
                            <!-- Academic Year -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="calendar-alt" class="mr-2 text-blue-600" />
                                    Academic Year <span class="text-red-500 ml-1">*</span>
                                </label>
                                <select v-model="form.academic_year"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                    <option value="">Select Academic Year</option>
                                    <option v-for="ay in academicYearsList" :key="ay" :value="ay">{{ ay }}</option>
                                </select>
                            </div>

                            <!-- Semester -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="calendar" class="mr-2 text-blue-600" />
                                    Semester <span class="text-red-500 ml-1">*</span>
                                </label>
                                <select v-model="form.semester"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                                    <option value="">Select Semester</option>
                                    <option v-for="s in semesterOptions" :key="s" :value="s">{{ s }}</option>
                                </select>
                            </div>

                            <!-- Preferred Location -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="map-marker-alt" class="mr-2 text-blue-600" />
                                    Preferred Location
                                </label>
                                <input v-model="form.preferred_location" type="text"
                                    placeholder="Enter preferred location (e.g., Dubai, Abu Dhabi)"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white" />
                            </div>

                            <!-- Expected Salary -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="dollar-sign" class="mr-2 text-blue-600" />
                                    Expected Salary
                                </label>
                                <input v-model="form.expected_salary" type="text"
                                    placeholder="Enter expected salary range (e.g., 5000-8000 AED)"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white" />
                            </div>

                            <!-- Preferred Employers -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="building" class="mr-2 text-blue-600" />
                                    Preferred Employers
                                </label>
                                <p class="text-xs text-gray-500 mb-3">Select one or more preferred employers from our partners list</p>
                                <div v-if="partnersLoading" class="text-center py-4">
                                    <font-awesome-icon icon="spinner" class="animate-spin text-blue-600 text-2xl" />
                                    <p class="text-sm text-gray-500 mt-2">Loading partners...</p>
                                </div>
                                <div v-else-if="partnersError" class="text-center py-4">
                                    <p class="text-sm text-red-600">Failed to load partners. Please try again.</p>
                                </div>
                                <multiselect
                                    v-else
                                    v-model="form.selectedPartners"
                                    :options="availablePartners"
                                    :multiple="true"
                                    :close-on-select="false"
                                    :clear-on-select="false"
                                    :preserve-search="true"
                                    track-by="id"
                                    :custom-label="partnerLabel"
                                    placeholder="Select preferred employers"
                                    :disabled="partnersLoading"
                                    class="w-full"
                                >
                                    <template #noResult>
                                        <span class="text-sm text-gray-500">No partners found</span>
                                    </template>
                                    <template #noOptions>
                                        <span class="text-sm text-gray-500">No partners available</span>
                                    </template>
                                </multiselect>
                                <p v-if="form.selectedPartners && form.selectedPartners.length > 0" class="text-xs text-gray-500 mt-2">
                                    {{ form.selectedPartners.length }} employer(s) selected
                                </p>
                            </div>

                            <!-- Additional Notes -->
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="sticky-note" class="mr-2 text-blue-600" />
                                    Additional Notes
                                </label>
                                <textarea v-model="form.additional_notes" rows="4"
                                    placeholder="Any additional information about your job preferences..."
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white resize-none"></textarea>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
                <base-button type="button" @click="closeForm" class="bg-gray-200 hover:bg-gray-300 text-gray-700">
                    Cancel
                </base-button>
                <base-button type="submit" :disabled="isSubmitting || !isLookingForJobSelected"
                    class="bg-blue-600 hover:bg-blue-700 text-white">
                    <font-awesome-icon v-if="isSubmitting" icon="spinner" class="animate-spin mr-2" />
                    {{ isSubmitting ? 'Saving...' : (request ? 'Update Request' : 'Create Request') }}
                </base-button>
            </div>
        </form>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import Fuse from "fuse.js";

const GET_EMPLOYERS_LIST = gql`
    query ListEmployers($isActive: Boolean) {
        listEmployers(isActive: $isActive) {
            id
            name
        }
    }
`;

const CREATE_EMPLOYMENT_REQUEST_MUTATION = gql`
    mutation CreateEmploymentRequest($input: EmploymentRequestInput!) {
        createEmploymentRequest(input: $input) {
            error
            error_code
            message
            data {
                id
                is_looking_for_job
                academic_year
                semester
                preferred_location
                expected_salary
                additional_notes
                partners {
                    id
                    en_company_name
                    company_short_name
                }
                created_at
                updated_at
            }
        }
    }
`;

const UPDATE_EMPLOYMENT_REQUEST_MUTATION = gql`
    mutation UpdateEmploymentRequest($id: ID!, $input: EmploymentRequestInput!) {
        updateEmploymentRequest(id: $id, input: $input) {
            error
            error_code
            message
            data {
                id
                is_looking_for_job
                academic_year
                semester
                preferred_location
                expected_salary
                additional_notes
                partners {
                    id
                    en_company_name
                    company_short_name
                }
                created_at
                updated_at
            }
        }
    }
`;

const GET_ACTIVE_PARTNERS_QUERY = gql`
    query GetActivePartners {
        activePartners {
            error
            error_code
            message
            data {
                id
                en_company_name
                company_short_name
            }
        }
    }
`;

const GET_ACADEMIC_YEARS_QUERY = gql`
    query GetAcademicYears($student_status: String, $entity: String) {
        getAcademicYears(student_status: $student_status, entity: $entity)
    }
`;

const SEMESTER_OPTIONS = ['Semester 1', 'Semester 2', 'Semester 3', 'Semester 4', 'Semester 5', 'Semester 6', 'Semester 7', 'Semester 8', 'Semester 9', 'Semester 10'];

export default {
    name: 'EmploymentRequestFormModal',
    components: {
        Multiselect,
    },
    props: {
        request: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            form: {
                is_looking_for_job: null,
                academic_year: '',
                semester: '',
                preferred_location: '',
                expected_salary: '',
                additional_notes: '',
                selectedPartners: [],
                not_looking_status: '',
                employerVal: null,
                otherEmployer: '',
                sector: '',
                location: '',
                position: '',
                salary_range: '',
                job_offers_count: 0,
                interviews_count: 0,
                got_job_within: '',
                is_job_rel_major: null,
                contract_type: '',
                start_date: '',
                end_date: '',
                is_currently_working: false,
                job_resource: '',
                job_resource_other: '',
                job_letter: null,
            },
            isSubmitting: false,
            availablePartners: [],
            partnersLoading: false,
            partnersError: false,
            listEmployers: [],
            employerOtherConfirmedDifferent: false,
            academicYearsList: [],
        };
    },
    computed: {
        semesterOptions() {
            return SEMESTER_OPTIONS;
        },
        isLookingForJobSelected() {
            return this.form.is_looking_for_job === true || this.form.is_looking_for_job === false;
        },
        isOtherEmployer() {
            return this.form.employerVal && this.form.employerVal.name === 'Other';
        },
        employerIsValid() {
            if (this.employerOtherConfirmedDifferent) return true;
            if (!this.form.employerVal) return true;
            if (this.form.employerVal.name !== 'Other') return true;
            const entered = (this.form.otherEmployer || '').trim();
            if (!entered) return false;
            const existingNames = this.listEmployers
                .filter(e => e.name && e.name !== 'Other')
                .map(e => e.name);
            const exactMatch = existingNames.find(
                n => n.trim().toLowerCase() === entered.toLowerCase()
            );
            if (exactMatch) return false;
            const fuse = new Fuse(
                this.listEmployers.filter(e => e.name && e.name !== 'Other'),
                { keys: ['name'], threshold: 0.4 }
            );
            const results = fuse.search(entered);
            return results.length === 0;
        },
        employerErrorMsg() {
            if (!this.form.employerVal) return '';
            if (this.form.employerVal.name !== 'Other') return '';
            const entered = (this.form.otherEmployer || '').trim();
            if (!entered) return 'Employer must not be empty.';
            const existingNames = this.listEmployers
                .filter(e => e.name && e.name !== 'Other')
                .map(e => e.name);
            const exactMatch = existingNames.find(
                n => n.trim().toLowerCase() === entered.toLowerCase()
            );
            if (exactMatch) return 'This employer already exists.';
            const fuse = new Fuse(
                this.listEmployers.filter(e => e.name && e.name !== 'Other'),
                { keys: ['name'], threshold: 0.4 }
            );
            const results = fuse.search(entered);
            if (results.length > 0) {
                const matchedNames = results.map(r => r.item.name);
                return `Employers with similar names exist: ${matchedNames.join(', ')}. Do you want to use one of these or continue with your entry?`;
            }
            return '';
        },
        employerSimilarNames() {
            if (this.employerOtherConfirmedDifferent) return [];
            if (!this.form.employerVal || this.form.employerVal.name !== 'Other') return [];
            const entered = (this.form.otherEmployer || '').trim();
            if (!entered) return [];
            const fuse = new Fuse(
                this.listEmployers.filter(e => e.name && e.name !== 'Other'),
                { keys: ['name'], threshold: 0.4 }
            );
            const results = fuse.search(entered);
            return results.length > 0 ? results.map(r => r.item.name) : [];
        },
    },
    watch: {
        request: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.form = {
                        is_looking_for_job:
                            newVal.is_looking_for_job === true
                                ? true
                                : newVal.is_looking_for_job === false
                                    ? false
                                    : null,
                        academic_year: newVal.academic_year || '',
                        semester: newVal.semester || '',
                        preferred_location: newVal.preferred_location || '',
                        expected_salary: newVal.expected_salary || '',
                        additional_notes: newVal.additional_notes || '',
                        selectedPartners: this.mapPartnersToSelected(newVal.partners),
                        not_looking_status: '',
                        employerVal: null,
                        otherEmployer: '',
                        sector: '',
                        location: '',
                        position: '',
                        salary_range: '',
                        job_offers_count: 0,
                        interviews_count: 0,
                        got_job_within: '',
                        is_job_rel_major: null,
                        contract_type: '',
                        start_date: '',
                        end_date: '',
                        is_currently_working: false,
                        job_resource: '',
                        job_resource_other: '',
                        job_letter: null,
                    };
                } else {
                    this.resetForm();
                }
            },
        },
        availablePartners: {
            handler() {
                // When partners are loaded and we're editing, sync selected partners
                if (this.request && this.request.partners) {
                    this.form.selectedPartners = this.mapPartnersToSelected(this.request.partners);
                }
            },
        },
        'form.employerVal'(newVal) {
            if (newVal && newVal.name !== 'Other') {
                this.form.otherEmployer = '';
            }
            this.employerOtherConfirmedDifferent = false;
        },
        'form.otherEmployer'() {
            this.employerOtherConfirmedDifferent = false;
        },
        'form.is_looking_for_job'(newVal) {
            if (newVal === true) {
                this.form.academic_year = '';
                this.form.semester = '';
                this.form.not_looking_status = '';
                this.form.employerVal = null;
                this.form.otherEmployer = '';
                this.form.sector = '';
                this.form.location = '';
                this.form.position = '';
                this.form.salary_range = '';
                this.form.job_offers_count = 0;
                this.form.interviews_count = 0;
                this.form.got_job_within = '';
                this.form.is_job_rel_major = null;
                this.form.contract_type = '';
                this.form.start_date = '';
                this.form.end_date = '';
                this.form.is_currently_working = false;
                this.form.job_resource = '';
                this.form.job_resource_other = '';
                this.form.job_letter = null;
            }
        },
        'form.is_currently_working'(newVal) {
            if (newVal === true) {
                this.form.end_date = '';
            }
        },
        'form.academic_year'() {
            this.form.semester = '';
        },
    },
    created() {
        this.loadPartners();
        this.loadEmployers();
        this.loadAcademicYears();
    },
    methods: {
        async loadEmployers() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_EMPLOYERS_LIST,
                    variables: { isActive: true },
                    fetchPolicy: 'network-only',
                });
                this.listEmployers = [...(data.listEmployers || []), { __typename: 'Employer', id: 0, name: 'Other' }];
            } catch (err) {
                console.error('Failed to fetch employers:', err);
            }
        },
        async loadAcademicYears() {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_ACADEMIC_YEARS_QUERY,
                    variables: { student_status: null, entity: null },
                    fetchPolicy: 'network-only',
                });
                this.academicYearsList = data.getAcademicYears || [];
            } catch (err) {
                console.error('Failed to fetch academic years:', err);
                this.academicYearsList = [];
            }
        },
        customLabelEmployer(obj) {
            return obj && obj.name ? obj.name : '';
        },
        confirmEmployerOtherDifferent() {
            this.employerOtherConfirmedDifferent = true;
        },
        validateEmployerOther() {
            if (this.employerOtherConfirmedDifferent) return true;
            return this.employerIsValid;
        },
        async loadPartners() {
            this.partnersLoading = true;
            this.partnersError = false;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_ACTIVE_PARTNERS_QUERY,
                    fetchPolicy: 'network-only',
                });

                if (data.activePartners.error) {
                    this.partnersError = true;
                    console.error('Failed to load partners:', data.activePartners.message);
                } else {
                    this.availablePartners = data.activePartners.data || [];
                }
            } catch (error) {
                this.partnersError = true;
                console.error('Failed to load partners:', error);
            } finally {
                this.partnersLoading = false;
            }
        },
        resetForm() {
            this.form = {
                is_looking_for_job: null,
                academic_year: '',
                semester: '',
                preferred_location: '',
                expected_salary: '',
                additional_notes: '',
                selectedPartners: [],
                not_looking_status: '',
                employerVal: null,
                otherEmployer: '',
                sector: '',
                location: '',
                position: '',
                salary_range: '',
                job_offers_count: 0,
                interviews_count: 0,
                got_job_within: '',
                is_job_rel_major: null,
                contract_type: '',
                start_date: '',
                end_date: '',
                is_currently_working: false,
                job_resource: '',
                job_resource_other: '',
                job_letter: null,
            };
        },
        partnerLabel(partner) {
            return partner.en_company_name || partner.company_short_name || 'Unknown Partner';
        },
        mapPartnersToSelected(partners) {
            if (!partners || !Array.isArray(partners) || partners.length === 0) {
                return [];
            }
            return partners.map(p => {
                const found = this.availablePartners.find(ap => ap.id === p.id);
                if (found) {
                    return found;
                }
                // If partner not found in available list, return the partner object as-is
                return {
                    id: p.id,
                    en_company_name: p.en_company_name,
                    company_short_name: p.company_short_name,
                };
            });
        },
        handleJobLetter(evt) {
            const files = evt && evt.target && evt.target.files;
            if (files && files.length > 0) {
                this.form.job_letter = files[0];
            } else {
                this.form.job_letter = null;
            }
        },
        validateEmploymentFields() {
            if (this.form.is_looking_for_job === true) {
                if (!(this.form.academic_year && this.form.academic_year.trim())) {
                    this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Academic Year is required.' });
                    return false;
                }
                if (!(this.form.semester && this.form.semester.trim())) {
                    this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Semester is required.' });
                    return false;
                }
            }
            if (this.form.is_looking_for_job !== false) return true;
            if (!this.form.not_looking_status) {
                this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Please select Employed or Self-Employed / Business Owner.' });
                return false;
            }
            if (!this.form.employerVal) {
                this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Employer is required.' });
                return false;
            }
            if (this.form.employerVal.name === 'Other') {
                if (!(this.form.otherEmployer && this.form.otherEmployer.trim())) {
                    this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'Employer must not be empty.' });
                    return false;
                }
                if (!this.validateEmployerOther()) {
                    this.$swal.fire({ icon: 'error', title: 'Validation Error', text: this.employerErrorMsg || 'Please fix the employer validation.' });
                    return false;
                }
            }
            const required = [
                { val: this.form.sector, msg: 'Sector is required.' },
                { val: this.form.location, msg: 'Location is required.' },
                { val: this.form.position && this.form.position.trim(), msg: 'Position is required.' },
                { val: this.form.salary_range, msg: 'Salary Range is required.' },
                { val: this.form.got_job_within, msg: 'Got Job Within is required.' },
                { val: this.form.is_job_rel_major === true || this.form.is_job_rel_major === false, msg: 'Job related to the major is required.' },
                { val: this.form.contract_type, msg: 'Contract Type is required.' },
                { val: this.form.start_date, msg: 'Start Date is required.' },
                { val: this.form.job_resource, msg: 'How did you get this job is required.' },
            ];
            for (let i = 0; i < required.length; i++) {
                if (!required[i].val) {
                    this.$swal.fire({ icon: 'error', title: 'Validation Error', text: required[i].msg });
                    return false;
                }
            }
            if (!this.form.is_currently_working && !this.form.end_date) {
                this.$swal.fire({ icon: 'error', title: 'Validation Error', text: 'End Date is required when not currently working here.' });
                return false;
            }
            return true;
        },
        async submitForm() {
            if (this.form.is_looking_for_job === null || this.form.is_looking_for_job === undefined) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Please specify if you are looking for a job.',
                });
                return;
            }
            if (!this.validateEmploymentFields()) {
                return;
            }

            this.isSubmitting = true;

            try {
                const input = {
                    is_looking_for_job: this.form.is_looking_for_job,
                    academic_year: this.form.is_looking_for_job && this.form.academic_year && this.form.academic_year.trim() ? this.form.academic_year.trim() : null,
                    semester: this.form.is_looking_for_job && this.form.semester && this.form.semester.trim() ? this.form.semester.trim() : null,
                    preferred_location: this.form.is_looking_for_job ? (this.form.preferred_location && this.form.preferred_location.trim() ? this.form.preferred_location.trim() : null) : null,
                    expected_salary: this.form.is_looking_for_job ? (this.form.expected_salary && this.form.expected_salary.trim() ? this.form.expected_salary.trim() : null) : null,
                    additional_notes: this.form.is_looking_for_job ? (this.form.additional_notes && this.form.additional_notes.trim() ? this.form.additional_notes.trim() : null) : null,
                    partner_ids: this.form.is_looking_for_job && this.form.selectedPartners && this.form.selectedPartners.length > 0
                        ? this.form.selectedPartners.map(p => p.id)
                        : [],
                };

                if (this.form.is_looking_for_job === false) {
                    input.not_looking_status = this.form.not_looking_status;
                    input.employer = this.form.employerVal && this.form.employerVal.name !== 'Other'
                        ? (this.form.employerVal.name || null)
                        : (this.form.otherEmployer && this.form.otherEmployer.trim() ? this.form.otherEmployer.trim() : null);
                    input.sector = this.form.sector || null;
                    input.location = this.form.location || null;
                    input.position = this.form.position && this.form.position.trim() ? this.form.position.trim() : null;
                    input.salary_range = this.form.salary_range || null;
                    input.job_offers_count = this.form.job_offers_count !== '' && this.form.job_offers_count !== null ? parseInt(this.form.job_offers_count, 10) : 0;
                    input.interviews_count = this.form.interviews_count !== '' && this.form.interviews_count !== null ? parseInt(this.form.interviews_count, 10) : 0;
                    input.got_job_within = this.form.got_job_within || null;
                    input.is_job_rel_major = this.form.is_job_rel_major === true || this.form.is_job_rel_major === false ? this.form.is_job_rel_major : null;
                    input.contract_type = this.form.contract_type || null;
                    input.start_date = this.form.start_date || null;
                    input.end_date = this.form.is_currently_working ? null : (this.form.end_date || null);
                    input.is_currently_working = this.form.is_currently_working === true;
                    input.job_resource = this.form.job_resource || null;
                    input.job_resource_other = this.form.job_resource === 'Other' && this.form.job_resource_other ? this.form.job_resource_other.trim() : null;
                    input.job_letter = this.form.job_letter || null;
                }

                let mutation;
                let variables;

                if (this.request) {
                    // Update existing request
                    mutation = UPDATE_EMPLOYMENT_REQUEST_MUTATION;
                    variables = {
                        id: this.request.id,
                        input: input,
                    };
                } else {
                    // Create new request
                    mutation = CREATE_EMPLOYMENT_REQUEST_MUTATION;
                    variables = {
                        input: input,
                    };
                }

                const { data } = await this.$apollo.mutate({
                    mutation: mutation,
                    variables: variables,
                });

                const result = this.request ? data.updateEmploymentRequest : data.createEmploymentRequest;

                if (result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Failed!',
                        text: result.message || 'Failed to save employment request',
                    });
                } else {
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: this.request ? 'Request updated successfully' : 'Request created successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        timerProgressBar: true,
                    });
                    this.$emit('saved');
                }
            } catch (error) {
                console.error('Submit employment request error:', error);
                let errorMessage = 'Failed to save employment request';

                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        closeForm() {
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
/* Vue Multiselect custom styling */
::v-deep .multiselect {
    min-height: 48px;
}

::v-deep .multiselect__tags {
    min-height: 48px;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 8px 40px 0 8px;
}

::v-deep .multiselect__tags:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
    font-size: 0.875rem;
    padding: 0;
    margin-bottom: 0;
    background: transparent;
    border: none;
}

::v-deep .multiselect__placeholder {
    padding-top: 8px;
    padding-bottom: 8px;
    color: #6b7280;
    font-size: 0.875rem;
}

::v-deep .multiselect__tag {
    background: #3b82f6;
    border-radius: 0.375rem;
    margin-bottom: 4px;
    margin-right: 4px;
    padding: 4px 8px;
    font-size: 0.75rem;
}

::v-deep .multiselect__tag-icon {
    border-radius: 0.375rem;
}

::v-deep .multiselect__tag-icon:hover {
    background: #2563eb;
}

::v-deep .multiselect__option {
    padding: 8px 12px;
    font-size: 0.875rem;
}

::v-deep .multiselect__option--highlight {
    background: #3b82f6;
}

::v-deep .multiselect__option--selected {
    background: #dbeafe;
    color: #1e40af;
    font-weight: 500;
}

::v-deep .multiselect__option--selected.multiselect__option--highlight {
    background: #3b82f6;
    color: white;
}

.form-fade-enter-active,
.form-fade-leave-active {
    transition: opacity 0.2s ease;
}

.form-fade-enter-from,
.form-fade-leave-to {
    opacity: 0;
}
</style>
